// Default Project Data - Disciplines with their associated DoDs
// Each discipline has an id, name, color, order, and doDs array

// Color palette for disciplines - distinct colors for visual differentiation
const DEFAULT_COLORS = [
  "#9b59b6", // Purple
  "#27ae60", // Green
  "#e67e22", // Orange
  "#3498db", // Blue
  "#e74c3c", // Red
  "#f39c12", // Yellow/Orange
  "#1abc9c", // Teal
  "#16a085", // Dark Teal
  "#34495e", // Dark Blue/Gray
  "#95a5a6", // Gray
  "#d35400", // Dark Orange
  "#c0392b", // Dark Red
  "#8e44ad", // Dark Purple
  "#2980b9", // Dark Blue
  "#2ecc71", // Green (replaced duplicate)
  "#7f8c8d" // Extra to avoid reuse on build_release
];

// Helper to get a color by index (cycling through colors)
const getColorByIndex = (index) => {
  return DEFAULT_COLORS[index % DEFAULT_COLORS.length];
};

// Helper to create DoD objects
const createDoD = (id, name, description) => ({
  id,
  name,
  description
});

// Define DoD sets that can be reused across disciplines
const DoDSets = {
  code: [
    createDoD("code_prototype", "Prototype", "Core mechanic implemented and playable.\n• No blocking bugs or crashes.\n• Code pushed, builds successfully."),
    createDoD("code_presentable", "Presentable", "Handles expected edge cases.\n• Code reviewed and merged into main branch.\n• Configurable via inspector or data table.\n• Performance acceptable for target platform."),
    createDoD("code_shippable", "Shippable", "No critical or major bugs.\n• Unit and integration tests pass.\n• Meets performance and memory budgets.\n• Code documented and tagged for release.")
  ],
  art: [
    createDoD("art_prototype", "Prototype", "Greybox or placeholder visuals convey function.\n• Scaled and positioned correctly in-engine."),
    createDoD("art_presentable", "Presentable", "Final concept approved.\n• Textures, materials, and lighting consistent with art style.\n• Asset named and stored in correct structure."),
    createDoD("art_shippable", "Shippable", "Meets poly, texture, and shader budgets.\n• LODs, collisions, and import settings finalized.\n• No visible clipping or lighting errors.")
  ],
  animation: [
    createDoD("animation_prototype", "Prototype", "Key poses or blocked animation in engine.\n• Timing and transitions understandable."),
    createDoD("animation_presentable", "Presentable", "Final polish pass complete.\n• Root motion and pivot aligned.\n• Blend tree or state machine setup validated."),
    createDoD("animation_shippable", "Shippable", "Animation clean, looping correctly.\n• No foot sliding or snapping.\n• Reviewed in full gameplay context.")
  ],
  design: [
    createDoD("design_prototype", "Prototype", "Core loop functional and testable.\n• Basic tuning variables exposed."),
    createDoD("design_presentable", "Presentable", "Metrics tracked and balanced for playtest.\n• Documentation updated in design sheet or wiki."),
    createDoD("design_shippable", "Shippable", "Feature validated by internal QA and playtest feedback.\n• Difficulty, pacing, and rewards tuned.")
  ],
  audio: [
    createDoD("audio_prototype", "Prototype", "Placeholder sound communicates intent."),
    createDoD("audio_presentable", "Presentable", "Final assets integrated and mixed to temp balance.\n• Cues triggered correctly in-game."),
    createDoD("audio_shippable", "Shippable", "Final mix approved by Audio Lead.\n• Peak levels within platform limits.\n• No missing or doubled cues.")
  ],
  vfx: [
    createDoD("vfx_prototype", "Prototype", "Timing and space coverage correct.\n• Reads clearly in gameplay camera."),
    createDoD("vfx_presentable", "Presentable", "Final materials and lighting tuned.\n• Performance tested on target hardware."),
    createDoD("vfx_shippable", "Shippable", "Clean particle culling and LOD setup.\n• No overdraw or flicker issues.")
  ],
  qa: [
    createDoD("qa_prototype", "Prototype", "Smoke checklist defined and run once.\n• Test environment available.\n• Critical paths captured as test cases."),
    createDoD("qa_presentable", "Presentable", "Feature test cases executed.\n• Top issues filed with repro steps.\n• CI smoke automated for this feature."),
    createDoD("qa_shippable", "Shippable", "Build passes smoke test.\n• Regression checklist cleared.\n• Issue tracker shows no open criticals for this feature.")
  ],
  narrative: [
    createDoD("narrative_prototype", "Prototype", "Core narrative concept defined (theme, tone, POV).\n• Placeholder text implemented and visible in game."),
    createDoD("narrative_presentable", "Presentable", "Story arcs, dialogue trees, and key moments written and reviewed.\n• Voice or text pacing verified in-engine.\n• Localization-ready script versioned and documented."),
    createDoD("narrative_shippable", "Shippable", "All text, dialogue, and VO recorded and implemented.\n• Localization integrated and verified in context.\n• Narrative QA complete: continuity, grammar, timing.")
  ],
  overhead: [
    createDoD("overhead_prototype", "Prototype", "Scope and milestones outlined.\n• Task tracking structure established (Jira, Confluence, etc)."),
    createDoD("overhead_presentable", "Presentable", "Schedule and velocity metrics stable for 2+ sprints.\n• Dependencies tracked and communicated.\n• Team retros and syncs consistently executed."),
    createDoD("overhead_shippable", "Shippable", "All deliverables verified as complete per DoD.\n• Retrospective and postmortem documented.\n• Handover documentation finalized.")
  ],
  marketing: [
    createDoD("marketing_prototype", "Prototype", "Core audience and positioning defined.\n• Branding, key art, and tone guide drafted."),
    createDoD("marketing_presentable", "Presentable", "Announcement or teaser materials produced.\n• Channels (Steam, site, socials) configured and active.\n• Copy reviewed for clarity and compliance."),
    createDoD("marketing_shippable", "Shippable", "Trailer, screenshots, and assets approved and live.\n• Store listings complete and optimized.\n• Community feedback loops active and monitored.")
  ],
  build: [
    createDoD("build_prototype", "Prototype", "Basic CI/CD pipeline set up.\n• Automated builds running for at least one platform."),
    createDoD("build_presentable", "Presentable", "Continuous builds stable across all target platforms.\n• Branching strategy enforced and documented.\n• Crash reporting and diagnostics integrated."),
    createDoD("build_shippable", "Shippable", "Release process automated and reproducible.\n• Build verified for versioning, signing, and distribution.\n• Monitoring and rollback procedures validated.")
  ],
  analytics: [
    createDoD("analytics_prototype", "Prototype", "Event plan drafted.\n• Core events fire locally.\n• IDs and versions included."),
    createDoD("analytics_presentable", "Presentable", "Events validated in staging.\n• Dashboards for KPIs.\n• Privacy flags respected."),
    createDoD("analytics_shippable", "Shippable", "Data quality checks pass.\n• Retention and funnel reports live.\n• PII handling verified.")
  ],
  security: [
    createDoD("sec_prototype", "Prototype", "Threat model draft.\n• Secrets not in repo.\n• Basic auth in place."),
    createDoD("sec_presentable", "Presentable", "Dependency and vuln scans in CI.\n• RBAC applied.\n• Audit logging enabled."),
    createDoD("sec_shippable", "Shippable", "Pen test issues resolved.\n• DPA/ToS ready.\n• Compliance checklist signed.")
  ],
  operations: [
    createDoD("ops_prototype", "Prototype", "Hiring plan and roles defined.\n• Onboarding checklist drafted."),
    createDoD("ops_presentable", "Presentable", "Onboarding executed.\n• Policies documented.\n• Incident comms template ready."),
    createDoD("ops_shippable", "Shippable", "Org chart current.\n• Knowledge base complete.\n• Handover documented.")
  ],
  customerSuccess: [
    createDoD("cs_prototype", "Prototype", "Ticketing set up.\n• Macro replies for top 5 issues."),
    createDoD("cs_presentable", "Presentable", "SLAs defined.\n• Feedback routing to backlog."),
    createDoD("cs_shippable", "Shippable", "Self-serve help center live.\n• CSAT tracking running.")
  ],
  sales: [
    createDoD("sales_prototype", "Prototype", "ICP and value props drafted.\n• Demo script v1."),
    createDoD("sales_presentable", "Presentable", "Deck and one-pagers approved.\n• CRM configured."),
    createDoD("sales_shippable", "Shippable", "Case studies and ROI calc live.\n• Win/loss loop to product.")
  ],
  moderation: [
    createDoD("mod_prototype", "Prototype", "Policy draft and flagging path.\n• Basic report flow works."),
    createDoD("mod_presentable", "Presentable", "Reviewer guidelines.\n• Rate limits and auto-actions."),
    createDoD("mod_shippable", "Shippable", "Abuse metrics monitored.\n• Appeals process documented.")
  ],
  documentation: [
    createDoD("docs_prototype", "Prototype", "README and quickstart.\n• Public API surface listed."),
    createDoD("docs_presentable", "Presentable", "Versioned docs site.\n• Examples runnable."),
    createDoD("docs_shippable", "Shippable", "Changelogs and deprecation policy.\n• SDKs released.")
  ],
  dataEngineering: [
    createDoD("data_eng_prototype", "Prototype", "Data pipeline architecture drafted.\n• Source systems identified.\n• Basic ETL flow prototyped."),
    createDoD("data_eng_presentable", "Presentable", "Pipeline runs in staging.\n• Data quality checks defined.\n• Initial dashboards available."),
    createDoD("data_eng_shippable", "Shippable", "Production pipeline monitored.\n• SLAs met for freshness and completeness.\n• BI reports validated and accessible.")
  ],
  legal: [
    createDoD("legal_prototype", "Prototype", "Legal requirements identified.\n• Terms of service draft.\n• Basic compliance checklist."),
    createDoD("legal_presentable", "Presentable", "Legal review completed.\n• Privacy policy and ToS approved.\n• Financial controls documented."),
    createDoD("legal_shippable", "Shippable", "All legal documents signed and live.\n• Compliance verified.\n• Rights management in place.")
  ]
};

// Helper to create a discipline with DoDs
const createDiscipline = (id, name, color, order, doDs = []) => ({
  id,
  name,
  color,
  order,
  doDs: doDs // Array order is the source of truth
});

export const getDefaultProjectData = (projectType) => {
  if (!projectType) return [];

  const disciplines = {
    video_game: [
      createDiscipline("video_game_game_design", "Game Design", getColorByIndex(0), 0, DoDSets.design),
      createDiscipline("video_game_programming", "Code", getColorByIndex(2), 2, DoDSets.code),
      createDiscipline("video_game_3d_art", "Art", getColorByIndex(3), 3, DoDSets.art),
      createDiscipline("video_game_animation", "Animation", getColorByIndex(4), 4, DoDSets.animation),
      createDiscipline("video_game_vfx", "VFX", getColorByIndex(5), 5, DoDSets.vfx),
      createDiscipline("video_game_audio", "Audio", getColorByIndex(6), 6, DoDSets.audio),
      createDiscipline("video_game_ui_ux", "UI/UX", getColorByIndex(7), 7, DoDSets.art),
    ],
    mobile_web_app: [
      createDiscipline("mobile_web_app_product_design", "Product Design", getColorByIndex(0), 0, DoDSets.design),
      createDiscipline("mobile_web_app_ux_research", "UX Research", getColorByIndex(1), 1, DoDSets.design),
      createDiscipline("mobile_web_app_ui_design", "UI Design", getColorByIndex(2), 2, DoDSets.art),
      createDiscipline("mobile_web_app_frontend", "Frontend Engineering", getColorByIndex(3), 3, DoDSets.code),
      createDiscipline("mobile_web_app_backend", "Backend Engineering", getColorByIndex(4), 4, DoDSets.code),
      createDiscipline("mobile_web_app_devops", "DevOps", getColorByIndex(5), 5, DoDSets.build),
      createDiscipline("mobile_web_app_qa", "QA", getColorByIndex(6), 6, DoDSets.qa),
      createDiscipline("mobile_web_app_content", "Content/Copywriting", getColorByIndex(7), 7, DoDSets.marketing),
      createDiscipline("mobile_web_app_data", "Analytics", getColorByIndex(8), 8, DoDSets.analytics),
      createDiscipline("mobile_web_app_marketing", "Marketing/Growth", getColorByIndex(9), 9, DoDSets.marketing),
      createDiscipline("mobile_web_app_legal", "Legal/Compliance", getColorByIndex(10), 10, [...DoDSets.security, ...DoDSets.legal]),
      createDiscipline("mobile_web_app_project_management", "Project Management", getColorByIndex(11), 11, DoDSets.overhead),
    ],
    saas_platform: [
      createDiscipline("saas_platform_product_management", "Product Management", getColorByIndex(0), 0, DoDSets.design),
      createDiscipline("saas_platform_ux_ui_design", "UX/UI Design", getColorByIndex(1), 1, DoDSets.art),
      createDiscipline("saas_platform_frontend", "Frontend Engineering", getColorByIndex(2), 2, DoDSets.code),
      createDiscipline("saas_platform_backend", "Backend / API Engineering", getColorByIndex(3), 3, DoDSets.code),
      createDiscipline("saas_platform_security", "Security / Compliance", getColorByIndex(4), 4, DoDSets.security),
      createDiscipline("saas_platform_devops", "DevOps / Infrastructure", getColorByIndex(5), 5, DoDSets.build),
      createDiscipline("saas_platform_data_engineering", "Data Engineering / BI", getColorByIndex(6), 6, DoDSets.dataEngineering),
      createDiscipline("saas_platform_customer_success", "Customer Success / Support", getColorByIndex(7), 7, DoDSets.customerSuccess),
      createDiscipline("saas_platform_sales", "Sales Enablement", getColorByIndex(8), 8, DoDSets.sales),
      createDiscipline("saas_platform_marketing", "Marketing / SEO / Content", getColorByIndex(9), 9, DoDSets.marketing),
      createDiscipline("saas_platform_legal", "Legal / Finance", getColorByIndex(10), 10, DoDSets.legal),
      createDiscipline("saas_platform_operations", "Operations / HR", getColorByIndex(11), 11, DoDSets.operations),
    ],
    digital_content_platform: [
      createDiscipline("digital_content_platform_content_acquisition", "Content Acquisition / Partnerships", getColorByIndex(0), 0, []), // No DoDs defined yet
      createDiscipline("digital_content_platform_ux_ui", "UX/UI Design", getColorByIndex(1), 1, DoDSets.art),
      createDiscipline("digital_content_platform_frontend", "Frontend Engineering", getColorByIndex(2), 2, DoDSets.code),
      createDiscipline("digital_content_platform_backend", "Backend / Media Engineering", getColorByIndex(3), 3, DoDSets.code),
      createDiscipline("digital_content_platform_data", "Data / Recommendation Systems", getColorByIndex(4), 4, DoDSets.dataEngineering),
      createDiscipline("digital_content_platform_video_encoding", "Video / Media Encoding", getColorByIndex(5), 5, DoDSets.build),
      createDiscipline("digital_content_platform_qa", "QA / Testing", getColorByIndex(6), 6, DoDSets.qa),
      createDiscipline("digital_content_platform_moderation", "Moderation / Trust & Safety", getColorByIndex(7), 7, DoDSets.moderation),
      createDiscipline("digital_content_platform_marketing", "Marketing / Influencer Relations", getColorByIndex(8), 8, DoDSets.marketing),
      createDiscipline("digital_content_platform_analytics", "Data Analytics / Retention Analysis", getColorByIndex(9), 9, DoDSets.analytics),
      createDiscipline("digital_content_platform_legal", "Legal / Rights Management", getColorByIndex(10), 10, DoDSets.legal),
      createDiscipline("digital_content_platform_operations", "Operations", getColorByIndex(11), 11, DoDSets.operations),
    ],
    ai_ml_product: [
      createDiscipline("ai_ml_product_data_engineering", "Data Engineering / ETL", getColorByIndex(0), 0, DoDSets.dataEngineering),
      createDiscipline("ai_ml_product_ml_research", "Machine Learning / Research", getColorByIndex(1), 1, []), // No DoDs defined yet
      createDiscipline("ai_ml_product_mlops", "MLOps / Infrastructure", getColorByIndex(2), 2, DoDSets.build),
      createDiscipline("ai_ml_product_backend", "Backend Integration", getColorByIndex(3), 3, DoDSets.code),
      createDiscipline("ai_ml_product_frontend", "Frontend / UX Engineering", getColorByIndex(4), 4, DoDSets.code),
      createDiscipline("ai_ml_product_ai_ethics", "AI Ethics / Policy", getColorByIndex(5), 5, DoDSets.security),
      createDiscipline("ai_ml_product_qa", "QA / Model Validation", getColorByIndex(6), 6, DoDSets.qa),
      createDiscipline("ai_ml_product_documentation", "Documentation / Developer Relations", getColorByIndex(7), 7, DoDSets.documentation),
      createDiscipline("ai_ml_product_marketing", "Marketing / Developer Evangelism", getColorByIndex(8), 8, DoDSets.marketing),
      createDiscipline("ai_ml_product_legal", "Legal / Compliance", getColorByIndex(9), 9, [...DoDSets.security, ...DoDSets.legal]),
      createDiscipline("ai_ml_product_product_management", "Product Management", getColorByIndex(10), 10, DoDSets.design),
    ],
  };

  return disciplines[projectType] || [];
};

// Helper to get just disciplines (for backward compatibility during migration)
export const getDefaultDisciplines = (projectType) => {
  return getDefaultProjectData(projectType).map(({ doDs, ...discipline }) => discipline);
};

// Helper to get all DoDs from all disciplines
export const getAllDoDs = (projectType) => {
  const disciplines = getDefaultProjectData(projectType);
  const allDoDs = [];
  disciplines.forEach(discipline => {
    discipline.doDs.forEach(dod => {
      // Only add if not already present (by id)
      if (!allDoDs.find(existing => existing.id === dod.id)) {
        allDoDs.push(dod);
      }
    });
  });
  // Return in the order they appear in disciplines (array order is source of truth)
  return allDoDs;
};

// Helper to get DoDs grouped by discipline
// Preserves the array order (which is the source of truth for ordering)
export const getDoDsByDiscipline = (disciplines) => {
  const grouped = {};
  disciplines.forEach(discipline => {
    if (discipline.doDs && discipline.doDs.length > 0) {
      // Use array order as-is - no sorting
      grouped[discipline.name] = discipline.doDs;
    }
  });
  return grouped;
};

